import { FC, useEffect } from "react";
import { Box, Typography } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  formatCountdown,
  formatStoreTime,
  useServerCountdown,
} from "../../../hooks/useServerCountdown";
import { getStoreStatusAction } from "../../../redux/actions/cashierStationActions";
import { Colors } from "../../../configs";

const WARNING_WINDOW_MS = 15 * 60 * 1000;
const STATUS_POLL_MS = 5 * 60 * 1000;

/**
 * Warns the cashier during the last 15 minutes before the store's scheduled
 * auto close, so they can close out and count the drawer themselves —
 * auto close records a zero closing balance.
 */
const AutoCloseWarningBanner: FC = () => {
  const dispatch = useAppDispatch();
  const storeId = useAppSelector((state) => state.auth.user?.storeId);
  const { activeStationCashier, getStoreStatusResponse } = useAppSelector(
    (state) => state.cashierStation
  );
  const isStationOpen = activeStationCashier?.status === "open";

  useEffect(() => {
    if (!storeId || !isStationOpen) return;
    dispatch(getStoreStatusAction(storeId));
    const interval = setInterval(
      () => dispatch(getStoreStatusAction(storeId)),
      STATUS_POLL_MS
    );
    return () => clearInterval(interval);
  }, [dispatch, storeId, isStationOpen]);

  const nextAutoCloseAt = isStationOpen
    ? getStoreStatusResponse?.nextAutoCloseAt
    : null;
  const remainingMs = useServerCountdown(
    nextAutoCloseAt,
    getStoreStatusResponse?.serverNow
  );

  if (
    !nextAutoCloseAt ||
    remainingMs === null ||
    remainingMs <= 0 ||
    remainingMs > WARNING_WINDOW_MS
  ) {
    return null;
  }

  return (
    <Box
      role="alert"
      data-testid="auto-close-warning-banner"
      sx={{
        position: "fixed",
        top: "1rem",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1500,
        backgroundColor: Colors.LIGHT_YELLOW,
        border: `0.1rem solid ${Colors.ERROR_RED}`,
        borderRadius: "0.7142857142857143rem",
        padding: "1rem 2rem",
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
      }}
    >
      <Typography
        sx={{
          color: Colors.TEXT_GRAY_DARK,
          fontSize: "var(--fs-base)",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        Station will auto close in {formatCountdown(remainingMs)} (
        {formatStoreTime(nextAutoCloseAt, getStoreStatusResponse?.storeTimeZone)}{" "}
        store time). Close out now to count your drawer.
      </Typography>
    </Box>
  );
};

export default AutoCloseWarningBanner;
